import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import AIPredictionPanel from './components/AIPredictionPanel';
import MarketDataGrid from './components/MarketDataGrid';
import TradingSignalsPanel from './components/TradingSignalsPanel';
import PortfolioSummary from './components/PortfolioSummary';
import InteractiveChart from './components/InteractiveChart';
import QuickActions from './components/QuickActions';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import useRealTimeMarketData from '../../hooks/useRealTimeMarketData';
import ConnectionStatusIndicator from '../../components/ui/ConnectionStatusIndicator';
import ConnectionDiagnostics from './components/ConnectionDiagnostics';

const TradingDashboard = () => {
  const [user, setUser] = useState(null);
  const [activeMarket, setActiveMarket] = useState('crypto'); // New state for market selection

  // Real-time market data integration
  const {
    marketStats,
    connectionStatus,
    lastUpdate,
    dataQuality,
    refresh,
    error,
    marketDataStatus
  } = useRealTimeMarketData(activeMarket);

  // Mock user data
  useEffect(() => {
    const mockUser = {
      name: 'Alex Johnson',
      email: 'alex.johnson@example.com',
      subscriptionTier: 'pro',
      avatar: 'https://randomuser.me/api/portraits/men/32.jpg'
    };
    setUser(mockUser);
  }, []);

  const handleLogin = () => {
    window.location.href = '/user-login';
  };

  const handleLogout = () => {
    setUser(null);
    window.location.href = '/user-login';
  };

  const handleUpgrade = () => {
    window.location.href = '/subscription-management';
  };

  const formatNumber = (num) => {
    if (num >= 1e12) return `$${(num / 1e12)?.toFixed(2)}T`;
    if (num >= 1e9) return `$${(num / 1e9)?.toFixed(2)}B`;
    if (num >= 1e6) return `$${(num / 1e6)?.toFixed(2)}M`;
    return `$${num?.toLocaleString()}`;
  };

  // Market switcher component
  const MarketSelector = () => (
    <div className="flex items-center space-x-1 bg-muted/20 rounded-lg p-1 mb-4">
      <button
        onClick={() => setActiveMarket('crypto')}
        className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
          activeMarket === 'crypto' ?'bg-primary text-primary-foreground' :'text-muted-foreground hover:text-card-foreground'
        }`}
      >
        <Icon name="Coins" size={16} className="inline mr-1" />
        Crypto
      </button>
      <button
        onClick={() => setActiveMarket('stocks')}
        className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
          activeMarket === 'stocks' ?'bg-primary text-primary-foreground' :'text-muted-foreground hover:text-card-foreground'
        }`}
      >
        <Icon name="TrendingUp" size={16} className="inline mr-1" />
        Stocks
      </button>
    </div>
  );

  const MarketStatusBar = () => {
    // Use real-time market stats when available, fallback to static data
    const currentStats = marketStats || {
      totalMarketCap: activeMarket === 'crypto' ? 1890000000000 : 45600000000000,
      totalVolume: activeMarket === 'crypto' ? 89500000000 : 156300000000,
      dominance: activeMarket === 'crypto' ? 52.3 : 0,
      dominanceSymbol: activeMarket === 'crypto' ? 'BTC' : '',
      sp500Change: activeMarket === 'stocks' ? 0.75 : undefined,
      vixIndex: activeMarket === 'stocks' ? 18.2 : undefined,
      fearGreedIndex: activeMarket === 'crypto' ? 67 : undefined
    };

    const marketStatus = connectionStatus === 'connected' ? 'active' : 'closed';
    
    return (
      <div className="bg-card border border-border rounded-lg p-4 mb-6 shadow-elevation-1">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-2">
              <div className={`w-2 h-2 rounded-full ${marketStatus === 'active' ? 'bg-success' : 'bg-error'}`}></div>
              <span className="text-sm font-medium text-card-foreground">
                {activeMarket === 'crypto' ? 'Crypto' : 'Stock'} Market {marketStatus === 'active' ? 'Live' : 'Offline'}
              </span>
              {connectionStatus && (
                <span className={`text-xs px-2 py-1 rounded ${
                  connectionStatus === 'connected' ? 'bg-success/10 text-success' :
                  connectionStatus === 'connecting'? 'bg-warning/10 text-warning' : 'bg-error/10 text-error'
                }`}>
                  {connectionStatus}
                </span>
              )}
            </div>
            <div className="hidden md:flex items-center space-x-6 text-sm">
              <div>
                <span className="text-muted-foreground">Market Cap: </span>
                <span className="font-medium text-card-foreground">{formatNumber(currentStats?.totalMarketCap)}</span>
              </div>
              <div>
                <span className="text-muted-foreground">24h Volume: </span>
                <span className="font-medium text-card-foreground">{formatNumber(currentStats?.totalVolume)}</span>
              </div>
              {activeMarket === 'crypto' ? (
                <>
                  <div>
                    <span className="text-muted-foreground">BTC Dominance: </span>
                    <span className="font-medium text-card-foreground">{currentStats?.dominance?.toFixed(1)}%</span>
                  </div>
                  {currentStats?.fearGreedIndex && (
                    <div>
                      <span className="text-muted-foreground">Fear & Greed: </span>
                      <span className="font-medium text-warning">{currentStats?.fearGreedIndex}</span>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {currentStats?.sp500Change !== undefined && (
                    <div>
                      <span className="text-muted-foreground">S&P 500: </span>
                      <span className={`font-medium ${currentStats?.sp500Change >= 0 ? 'text-success' : 'text-destructive'}`}>
                        {currentStats?.sp500Change >= 0 ? '+' : ''}{currentStats?.sp500Change}%
                      </span>
                    </div>
                  )}
                  {currentStats?.vixIndex && (
                    <div>
                      <span className="text-muted-foreground">VIX: </span>
                      <span className="font-medium text-warning">{currentStats?.vixIndex}</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-4">
            {dataQuality !== 'good' && (
              <span className={`text-xs px-2 py-1 rounded ${
                dataQuality === 'degraded' ? 'bg-warning/10 text-warning' : 'bg-error/10 text-error'
              }`}>
                {dataQuality} data
              </span>
            )}
            <span className="text-xs text-muted-foreground">
              {lastUpdate ? `Updated: ${lastUpdate?.toLocaleTimeString()}` : 'Not connected'}
            </span>
            <Button variant="ghost" size="sm" onClick={refresh} icon={<Icon name="RefreshCw" size={16} />}>
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const WelcomeSection = () => (
    <div className="mb-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground mb-2">
            Welcome back, {user?.name || 'Trader'}!
          </h1>
          <p className="text-muted-foreground">
            Your AI-powered investment prediction command center with real-time data feeds. 
            {user?.subscriptionTier === 'pro' ? ' Pro features are active.' : ' Upgrade for advanced features.'}
          </p>
        </div>
        <div className="hidden md:flex items-center space-x-3">
          {user?.subscriptionTier === 'free' && (
            <Button variant="outline" onClick={handleUpgrade}>
              <Icon name="Crown" size={16} />
              Upgrade to Pro
            </Button>
          )}
          <Button variant="default" onClick={() => window.location.href = '/ai-predictions-detail'}>
            <Icon name="Brain" size={16} />
            View AI Insights
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <Header 
        user={user} 
        title="Trading Dashboard"
        subtitle="AI-powered cryptocurrency trading platform"
        onLogin={handleLogin} 
        onLogout={handleLogout} 
        onUpgrade={handleUpgrade} 
      />
      
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Connection Status Bar */}
          <div className="mb-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="text-2xl font-bold text-foreground">Trading Dashboard</h1>
              
              {/* Market Selector */}
              <MarketSelector />
            </div>
            
            {/* Connection Status */}
            <div className="flex items-center gap-3">
              <ConnectionStatusIndicator
                connectionStatus={connectionStatus}
                dataQuality={dataQuality}
                lastUpdate={lastUpdate}
                error={error}
                fallbackActive={marketDataStatus?.fallbackActive}
                onRetry={refresh}
              />
            </div>
          </div>

          {/* Connection Diagnostics */}
          {(connectionStatus === 'error' || connectionStatus === 'degraded' || error) && (
            <div className="mb-6">
              <ConnectionDiagnostics
                connectionStatus={connectionStatus}
                diagnostics={marketDataStatus}
                error={error}
                onReconnect={refresh}
              />
            </div>
          )}

          <WelcomeSection />
          <MarketStatusBar />
          
          {/* Main Dashboard Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            {/* Left Column - AI Predictions */}
            <div className="lg:col-span-1">
              <AIPredictionPanel activeMarket={activeMarket} />
            </div>
            
            {/* Middle Column - Chart */}
            <div className="lg:col-span-2">
              <InteractiveChart activeMarket={activeMarket} />
            </div>
          </div>

          {/* Market Data Grid - Now with real-time data */}
          <div className="mb-6">
            <MarketDataGrid activeMarket={activeMarket} />
          </div>

          {/* Bottom Section */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Portfolio Summary */}
            <div className="lg:col-span-1">
              <PortfolioSummary activeMarket={activeMarket} />
            </div>
            
            {/* Trading Signals */}
            <div className="lg:col-span-1">
              <TradingSignalsPanel activeMarket={activeMarket} />
            </div>
            
            {/* Quick Actions */}
            <div className="lg:col-span-1">
              <QuickActions activeMarket={activeMarket} />
            </div>
          </div>

          {/* Mobile-specific Quick Access */}
          <div className="md:hidden mt-6">
            <div className="bg-card border border-border rounded-lg p-4">
              <h3 className="font-semibold text-card-foreground mb-3">Quick Access</h3>
              <div className="grid grid-cols-2 gap-3">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => window.location.href = '/portfolio-tracker'}
                  className="justify-start"
                >
                  <Icon name="PieChart" size={16} />
                  Portfolio
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => window.location.href = '/trading-signals'}
                  className="justify-start"
                >
                  <Icon name="Zap" size={16} />
                  Signals
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => window.location.href = '/market-analysis'}
                  className="justify-start"
                >
                  <Icon name="BarChart3" size={16} />
                  Analysis
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => window.location.href = '/ai-predictions-detail'}
                  className="justify-start"
                >
                  <Icon name="Brain" size={16} />
                  AI Insights
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TradingDashboard;